import { FlexibleScenario, Scenario } from "@reco/shared/src/constants";
import { isFeatureEnabled } from "@reco/shared/src/features";
import { getRuntimeConfig } from "@reco/shared/src/runtime-config";
import type { EnvironmentConfig } from "@reco/shared/src/types/config";
import type {
  Recommendation,
  RecommendationsArgs,
  RecommendationsConfig,
  RecommendationsData,
  RecommendationsResult,
  StandardRecommendationsResult,
} from "@reco/shared/src/types/recommendation";
import ABTest from "./ABTest";
import CriteoAPIService from "./CriteoAPIService";
import { getGridboxData } from "./GridboxAPIService";
import LocalRecommendations from "./LocalRecommendations";
import OneTrustConsent from "./OneTrustConsent";
import RecommendationsAPIService, { type RecommendationsRequestParams } from "./RecommendationsAPIService";
import type { TrackingInfoCookie } from "./types/cookies";
import { prepareResponse } from "./utils/api";
import { getEnvironmentConfig } from "./utils/config";
import { dispatchLoadEvent } from "./utils/events";
import { parseFlexibleScenario } from "./utils/flexibleScenarios";
import { getProductIDsBasedOnSource, getProductIDsFromVisitHistory, isShopTheLookScenario } from "./utils/helpers";
import { getContextId, getDefaultParams, getProductIdForCriteoRequest } from "./utils/params";
import { parseBase64Json } from "./utils/parsers";
import { clearVisitHistory, getCookieValue } from "./utils/storage";

type RecommendationServiceOptions = {
  recommendationsAPIService?: RecommendationsAPIService;
  localRecommendations?: LocalRecommendations;
  envConfig?: EnvironmentConfig;
};

type Action = (params: RecommendationsConfig) => RecommendationsData | Promise<RecommendationsData>;
type ActionsByScenario = Omit<Record<Scenario | FlexibleScenario, Action>, Scenario.CUSTOMERS>;

export default class RecommendationService {
  envConfig: EnvironmentConfig;
  recommendationsAPIService: RecommendationsAPIService;
  localRecommendations: LocalRecommendations;
  actionsByScenario: Partial<ActionsByScenario>;

  constructor(opts: RecommendationServiceOptions = {}) {
    this.recommendationsAPIService = opts.recommendationsAPIService ?? new RecommendationsAPIService();
    this.localRecommendations = opts.localRecommendations ?? new LocalRecommendations();
    this.envConfig = opts.envConfig ?? getEnvironmentConfig();
    this.actionsByScenario = {
      [Scenario.CART_CROSS]: this.getCartCross,
      [Scenario.CART_PRODUCTS]: this.getCartProducts,
      [Scenario.CARTOVERLAY]: this.getApiRecommendations,
      [Scenario.LAST_SEEN]: this.getLastSeen,
      [Scenario.PERSONALIZATION]: this.getPersonalization,
      [Scenario.PERSONAL_BRAND]: this.getPersonalScenarioBasedOnHistory,
      [Scenario.PERSONAL_CATEGORY]: this.getPersonalScenarioBasedOnHistory,
      [Scenario.BRANDS]: this.getApiRecommendations,
      [Scenario.BESTSELLERS]: this.getApiRecommendations,
      [Scenario.FLEXIBLE_BESTSELLERS]: this.getFlexibleBestsellers,
      [Scenario.CROSS]: this.getApiRecommendations,
      [Scenario.MULTICROSS]: this.getApiRecommendations,
      [Scenario.PRODUCTS]: this.getApiRecommendations,
      [Scenario.BUNDLES]: this.getApiRecommendations,
      [Scenario.COMPARISONS]: this.getApiRecommendations,
      [Scenario.SPONSORED]: this.getSponsoredRecommendations,
      [Scenario.SHOPTHELOOK]: this.getApiRecommendations,
      // Generic flexible scenario for PRECO-4029 - handles all dynamic flexible scenarios
      [FlexibleScenario.BESTSELLERS_BRAND_PRODUCTBASED_HISTORY]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_BRAND_PRODUCTBASED_URL]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_1_PRODUCTBASED_HISTORY]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_1_PRODUCTBASED_URL]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_2_PRODUCTBASED_HISTORY]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_2_PRODUCTBASED_URL]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_3_PRODUCTBASED_HISTORY]: this.getFlexibleScenarioRecommendations,
      [FlexibleScenario.BESTSELLERS_CATEGORY_3_PRODUCTBASED_URL]: this.getFlexibleScenarioRecommendations,
    };
  }

  public async getRecommendations(recoArgs: RecommendationsArgs): Promise<RecommendationsResult> {
    const params: RecommendationsConfig = getDefaultParams(this.envConfig, recoArgs);
    const recoApiResponse = await this.actionsByScenario[params.scenario].call(this, params);

    if (isShopTheLookScenario(params.scenario)) {
      dispatchLoadEvent(params.scenario, recoApiResponse.shopTheLookItems || []);
      return {
        shopTheLookItems: recoApiResponse.shopTheLookItems || [],
        locale: `${params.languageId}_${params.countryId}`,
        scenario: params.scenario,
        pageType: params.pageType,
        abTest: params.abTest,
        colorScheme: params.colorScheme,
      };
    }

    params.recommendationItems = recoApiResponse.recommendationItems;
    params.brand = recoApiResponse.brand;
    params.brandName = recoApiResponse.brandName;
    params.categoryPath = recoApiResponse.categoryPath;
    params.categoryPathName = recoApiResponse.categoryPathName;
    params.categoryName = recoApiResponse.categoryName;

    if (!params.recommendationItems?.length) {
      dispatchLoadEvent(params.scenario, []);
      return this.getFallbackRecommendations(params);
    }

    params.recommendations = await getGridboxData({
      recoDetailsHost: params.recoDetailsHost as string,
      countryId: params.countryId,
      languageId: params.languageId,
      recommendationItems: params.recommendationItems.map((item) => item.itemId),
    });

    if (!params.recommendations?.length) {
      dispatchLoadEvent(params.scenario, []);
      return this.getFallbackRecommendations(params);
    }

    params.recommendations = prepareResponse(params);
    dispatchLoadEvent(params.scenario, params.recommendations);

    return {
      recommendations: params.recommendations,
      recommendationItems: params.recommendationItems,
      locale: `${params.languageId}_${params.countryId}`,
      brandName: params.brandName,
      categoryPath: params.categoryPath,
      categoryPathName: params.categoryPathName,
      categoryName: params.categoryName,
      scenario: params.scenario,
      pageType: params.pageType,
      abTest: params.abTest,
      colorScheme: params.colorScheme,
    };
  }

  getFallbackRecommendations(params: RecommendationsConfig): StandardRecommendationsResult {
    return {
      recommendations: [],
      recommendationItems: [],
      locale: `${params.languageId}_${params.countryId}`,
      brand: params.brand,
      brandName: params.brandName,
      categoryPath: params.categoryPath,
      categoryPathName: params.categoryPathName,
      categoryName: params.categoryName,
      // NEOSRF-256: The Scenario SHOPTHELOOK is handled separately and never calls this method
      scenario: params.scenario as Exclude<Scenario, Scenario.SHOPTHELOOK>,
      pageType: params.pageType,
      colorScheme: params.colorScheme,
    };
  }

  private getCartCross(params: RecommendationsConfig): RecommendationsData {
    const recommendationItems = this.localRecommendations.getCart(
      params.requestLimit,
      params.contextId,
      params.exclude,
      Scenario.CROSS,
    );
    return { recommendationItems };
  }

  private getCartProducts(params: RecommendationsConfig): RecommendationsData {
    const recommendationItems = this.localRecommendations.getCart(
      params.requestLimit,
      params.contextId,
      params.exclude,
      Scenario.PRODUCTS,
    );
    return { recommendationItems };
  }

  private getLastSeen(params: RecommendationsConfig): RecommendationsData {
    params.contextId = getContextId(params.pageType, params.pathname);
    const recommendationItems = this.localRecommendations.getLastSeen(
      params.requestLimit,
      params.contextId,
      params.exclude,
    );
    return { recommendationItems };
  }

  private async getFlexibleBestsellers(params: RecommendationsConfig): Promise<RecommendationsData> {
    params.contextId = params.contextId || getContextId(params.pageType, params.pathname);
    const response = await this.recommendationsAPIService.get({
      ...params,
      scenario: Scenario.FLEXIBLE_BESTSELLERS,
    } as RecommendationsRequestParams);

    return { recommendationItems: response.recommendationItems };
  }

  private async getPersonalization(params: RecommendationsConfig): Promise<RecommendationsData> {
    const consent = await OneTrustConsent.getConsent(params.countryId);
    const marketingConsent = consent?.marketing;
    const trackingInfo = getCookieValue("tracking-info");
    const lidlId = getCookieValue("LidlID");

    const getLocalPersonalization = () => {
      params.contextId = getContextId(params.pageType, params.pathname);
      delete params.lidlId;
      delete params.ssoId;
      const recommendationItems = this.localRecommendations.getPersonalization(
        params.requestLimit,
        params.contextId,
        params.exclude,
      );
      return { recommendationItems };
    };

    if (marketingConsent && (trackingInfo || lidlId)) {
      if (trackingInfo) {
        const trackingInfoData = parseBase64Json<TrackingInfoCookie>(trackingInfo);
        params.ssoId = trackingInfoData?.ssoId;
      } else if (lidlId) {
        params.lidlId = lidlId;
      }

      params.contextId = params.contextId || getContextId(params.pageType, params.pathname);
      const response = await this.recommendationsAPIService.get({
        ...params,
        scenario: Scenario.CUSTOMERS,
      } as RecommendationsRequestParams);

      if (response.recommendationItems?.length >= 2) {
        return { recommendationItems: response.recommendationItems };
      } else {
        return getLocalPersonalization();
      }
    } else {
      return getLocalPersonalization();
    }
  }

  private async getApiRecommendations(params: RecommendationsConfig): Promise<RecommendationsData> {
    params.contextId = params.contextId || getContextId(params.pageType, params.pathname);

    const consent = OneTrustConsent.getConsentSync();
    const response = await this.recommendationsAPIService.get(params as RecommendationsRequestParams);

    let recommendationItems: Recommendation[] = [];

    if (consent?.marketing) {
      params.abTest = ABTest.handleABResponse(params.scenario, response.abTest);
      recommendationItems = params?.abTest?.recommendationItems || response.recommendationItems;
      if (Scenario.PRODUCTS === params.scenario && params.contextId) {
        try {
          this.localRecommendations.saveVisit({
            contextId: params.contextId,
            scenario: params.scenario,
            recommendationItems: recommendationItems,
            categoryPath: response.categoryPath,
            categoryPathName: response.categoryPathName,
            brand: response.brand,
            brandName: response.brandName,
          });
        } catch (e) {
          console.error("RecommendationService.getApiRecommendations: error saving visit", e);
        }
      }
    } else {
      recommendationItems = response.recommendationItems;
      ABTest.deleteABTestDb();
      clearVisitHistory();
    }

    if (params.exclude) {
      recommendationItems = recommendationItems.filter((reco) => params.exclude?.indexOf(reco.itemId) === -1);
    }

    let shopTheLookItems: RecommendationsData["shopTheLookItems"] = undefined;
    if (isShopTheLookScenario(params.scenario)) {
      // NEOSRF-256: Maximum of 12 items to be returned for SHOPTHELOOK scenario
      shopTheLookItems = response.shopTheLookItems?.slice(0, 12) || [];
    }

    return {
      brand: response.brand,
      brandName: response.brandName,
      categoryPath: response.categoryPath,
      categoryPathName: response.categoryPathName,
      categoryName: response.categoryName,
      recommendationItems,
      shopTheLookItems,
    };
  }

  private async getSponsoredRecommendations(params: RecommendationsConfig): Promise<RecommendationsData> {
    const emptyResponse = { recommendationItems: [] };

    if (!this.isCriteoEnabled(params.countryId)) {
      console.warn(`[Sponsored Slider]: CRITEO feature is not enabled for ${params.countryId}.`);
      return emptyResponse;
    }

    const consent = await OneTrustConsent.getConsent(params.countryId);
    if (!consent || !consent.marketing || !consent.statistics) {
      return emptyResponse;
    }

    const productID = getProductIdForCriteoRequest(params.pageType, params.pathname);
    const response = await CriteoAPIService.get(productID, params.countryId);
    let recommendationItems: Recommendation[] = response.recommendationItems;

    if (params.exclude) {
      recommendationItems = recommendationItems.filter((reco) => params.exclude?.indexOf(reco.itemId) === -1);
    }
    return {
      recommendationItems,
    };
  }

  private readonly getPersonalScenarioBasedOnHistory = async (
    params: RecommendationsConfig,
  ): Promise<RecommendationsData> => {
    const consent = await OneTrustConsent.getConsent(params.countryId);
    if (!consent?.marketing) {
      return { recommendationItems: [] };
    }

    return await this.getApiRecommendations({
      ...params,
      contextId: getProductIDsFromVisitHistory(),
    });
  };

  /**
   * Handles flexible scenario recommendations for PRECO-4029
   * Parses the data-type string and calls the new flexible API endpoint
   * @param params Recommendation configuration with flexible scenario
   * @returns Recommendation data for the provided flexible scenario configuration
   * @throws Error if there is an issue processing the flexible scenario, i.e., there was an error to get the product IDs from URL or localStorage
   */
  private async getFlexibleScenarioRecommendations(params: RecommendationsConfig): Promise<RecommendationsData> {
    try {
      const source = parseFlexibleScenario(params.type).source;

      return await this.getApiRecommendations({
        ...params,
        contextId: getProductIDsBasedOnSource(source, params),
      });
    } catch (error) {
      throw new Error("Error processing flexible scenario:" + error);
    }
  }

  private isCriteoEnabled(country: string) {
    const config = getRuntimeConfig();
    return isFeatureEnabled(config.features, country, "criteo");
  }
}
